import { useState } from 'react';
import { arrayMove } from 'react-movable';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useEditTag } from '#api/tags.queries.ts';
import { Spinner } from '#assets/Spinner.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { SortableList } from '#components/atoms/lists/sortable-list/SortableList.tsx';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import type { Tag } from '#dn-types/tag.ts';
import { TagReorderRow } from '../row/TagReorderRow.tsx';
import { calculateBeforeAfter } from './calculate-before-after.ts';
import styles from './TagReorderList.module.css';

type Props = {
  tags: Tag[];
};

type ReorderState =
  | { state: 'unchanged' }
  | { state: 'reordering' }
  | { state: 'reordered' }
  | { state: 'error'; errorMessage: string };

export function TagReorderList({ tags }: Props) {
  const [reorderState, setReorderState] = useState<ReorderState>({ state: 'unchanged' });
  const [tempTagsOrder, setTempTagsOrder] = useState<Tag[]>(tags);
  const reorder = useEditTag();

  // When we are waiting for a server response, show the tags the way they were dropped, to avoid flicker
  const tagRows = (reorderState.state === 'reordering' ? tempTagsOrder : tags).map((t) => (
    <TagReorderRow key={t.name} name={t.name} description={t.description} />
  ));

  async function handleChange(moveMeta: { oldIndex: number; newIndex: number }) {
    setTempTagsOrder(arrayMove(tempTagsOrder, moveMeta.oldIndex, moveMeta.newIndex));
    setReorderState({ state: 'reordering' });

    const { moved, moveType, reference } = calculateBeforeAfter(tags, moveMeta);

    try {
      // Send only the position: the list entries omit firewall rules and may be stale
      await reorder.mutateAsync({
        name: moved.name,
        variables: moveType === 'before' ? { before: reference.name } : { after: reference.name },
      });
      setReorderState({ state: 'reordered' });
    } catch (err) {
      trackError(err);
      if (err instanceof ValidationError) {
        handleServerFormError(err, (error) => {
          setReorderState({ state: 'error', errorMessage: error.message });
          return true;
        });
      } else {
        setReorderState({ state: 'error', errorMessage: 'We couldn’t reorder your tags, please try again.' });
      }
    }
  }

  return (
    <>
      <div className={styles.TagReorderList_header}>
        <div>Tag</div>
        <div>Description</div>
      </div>
      <SortableList rows={tagRows} onChange={handleChange} isSaving={reorderState.state === 'reordering'} />
      <ConfirmChangesBanner
        isOpen={reorderState.state !== 'unchanged'}
        title={
          <>
            {reorderState.state === 'error' ?
              undefined
            : <Spinner
                size={16}
                state={reorderState.state === 'reordering' ? 'spinning' : 'success'}
                className="mr-4"
              />
            }
            {reorderState.state === 'error' ?
              reorderState.errorMessage
            : reorderState.state === 'reordering' ?
              'Saving…'
            : 'Changes saved'}
          </>
        }
        primaryButton={<LinkButton to="/tags">Done</LinkButton>}
      />
    </>
  );
}
